/**
 * Task Enrichment Hook with Incremental Updates
 *
 * This module provides the useTaskEnrichment hook, which enriches tasks with
 * container hierarchy information (spaceName, folderPath, listName) and calculates
 * unique values for filtering.
 *
 * KEY OPTIMIZATION:
 * Uses incremental updates instead of full recalculation to avoid O(n) performance
 * cost on every render. With 8000+ tasks, this optimization significantly reduces
 * enrichment time by only processing changed tasks and reusing cached enriched
 * tasks for unchanged ones.
 *
 * The incremental update strategy uses:
 * - Hash-based change detection (task id:updatedAt)
 * - useRef caching of enriched tasks between renders
 * - Selective re-enrichment of only changed/new tasks
 *
 * @see useTaskEnrichment - Main hook export
 */
import { useMemo, useRef } from 'react';
import { getUniqueValues } from '@/utils/taskUtils';
import type { Task } from '@/types';

/**
 * Container information for task enrichment
 * Represents a space, folder, or list in the hierarchy
 */
interface Container {
  _id: string;
  type: 'space' | 'folder' | 'list';
  name: string;
  /** ID-based hierarchical path for filtering (e.g., "uuid1/uuid2/uuid3") */
  path: string;
  /** Name-based display path for UI (e.g., "Engineering/Backend/API") */
  displayPath?: string;
}

/**
 * Options for the useTaskEnrichment hook
 */
interface UseTaskEnrichmentOptions {
  /** Array of tasks to enrich */
  tasks: Task[];
  /** Map of containerId -> Container with path information (optional, null-safe) */
  containersMap?: Map<string, Container> | null;
}

/**
 * Task enriched with container hierarchy information
 * Extends base Task with additional derived fields
 */
type EnrichedTask = Task & {
  /** Full container path (e.g., "Engineering/Backend/API") */
  path?: string;
  /** First segment of path (e.g., "Engineering") */
  spaceName?: string;
  /** Middle segments of path (e.g., ["Backend"]) */
  folderPath?: string[];
  /** Last segment of path for list containers (e.g., "API") */
  listName?: string;
};

/**
 * Creates a hash for a task based on id and updatedAt
 * Used for detecting which tasks have changed since last enrichment
 *
 * HASH FORMAT: "taskId:updatedAt"
 * Example: "task_123:1640000000000"
 *
 * The updatedAt timestamp ensures that any update to the task
 * (which should update the timestamp) will change the hash,
 * triggering re-enrichment. If updatedAt is missing, uses "0"
 * as a fallback to ensure hash stability.
 *
 * @param task - Task to create hash for
 * @returns Hash string in format "id:updatedAt" for change detection
 */
function createTaskHash(task: Task): string {
  return `${task.id}:${task.updatedAt ?? '0'}`;
}

/**
 * Detects which tasks have changed by comparing current and previous task hashes
 * Follows hash-based change detection pattern from useSpaces (line 72)
 *
 * CHANGE DETECTION ALGORITHM:
 * 1. For each task in currentTasks:
 *    - Create hash from task (id:updatedAt format)
 *    - Store hash in new taskHashMap
 *    - Compare with previous hash from previousTasksMap
 * 2. If hashes differ (or task not in previous map):
 *    - Task is new or has been updated
 *    - Add task ID to changedTaskIds set
 * 3. If hashes match:
 *    - Task is unchanged since last enrichment
 *    - Skip (not added to changedTaskIds)
 *
 * The hash combines task.id and task.updatedAt, so any change to the task
 * (including field updates) will change the hash and trigger re-enrichment.
 *
 * @param currentTasks - Current tasks array
 * @param previousTasksMap - Map of previous task hashes (taskId -> "id:updatedAt" hash)
 * @returns Object with changedTaskIds set and new taskHashMap for next iteration
 */
function detectChangedTasks(
  currentTasks: Task[],
  previousTasksMap: Map<string, string>
): { changedTaskIds: Set<string>; taskHashMap: Map<string, string> } {
  const changedTaskIds = new Set<string>();
  const taskHashMap = new Map<string, string>();

  // Build new hash map and detect changes
  for (const task of currentTasks) {
    const currentHash = createTaskHash(task);
    taskHashMap.set(task.id, currentHash);

    const previousHash = previousTasksMap.get(task.id);
    if (previousHash !== currentHash) {
      // Task is new or has been updated
      changedTaskIds.add(task.id);
    }
  }

  return { changedTaskIds, taskHashMap };
}

/**
 * Enriches a single task with container information (path, spaceName, folderPath, listName)
 * Extracted as helper to avoid code duplication in incremental enrichment logic
 *
 * ENRICHMENT PROCESS:
 * 1. Validate task has spaceId (required for enrichment)
 * 2. Look up container in containersMap using task.spaceId
 * 3. Parse container.path to extract:
 *    - spaceName: first segment of path (e.g., "Engineering" from "Engineering/Backend/API")
 *    - folderPath: middle segments (e.g., ["Backend"] from "Engineering/Backend/API")
 *    - listName: last segment if container type is 'list' (e.g., "API")
 * 4. Return new enriched task object with additional fields
 *
 * If enrichment fails (no spaceId, container not found), returns original task unchanged.
 *
 * @param task - Task to enrich
 * @param containersMap - Map of containerId -> Container with path information
 * @returns Enriched task with path/spaceName/folderPath/listName, or original task if enrichment not possible
 */
function enrichSingleTask(task: Task, containersMap: Map<string, Container>): EnrichedTask {
  // Require spaceId to enrich task
  if (!task.spaceId || task.spaceId.trim() === '') {
    return task;
  }

  const container = containersMap.get(task.spaceId);
  if (!container) {
    return task;
  }

  // Extract spaceName, folderPath, listName from displayPath (name-based) for UI display
  const displayParts = (container.displayPath || container.path).split('/');
  const spaceName = displayParts[0];
  const folderPath = displayParts.length > 1 ? displayParts.slice(1, -1) : [];
  const listName = container.type === 'list' ? displayParts[displayParts.length - 1] : undefined;

  return {
    ...task,
    path: container.path, // ID-based for filtering
    spaceName,
    folderPath,
    listName,
  };
}

/**
 * Performs incremental enrichment by only processing changed tasks
 * Follows delta update pattern from taskCache.ts (lines 160-271)
 *
 * INCREMENTAL UPDATE LOGIC:
 * For each task in currentTasks:
 * 1. If task ID is in changedTaskIds:
 *    - Task is new or has been updated
 *    - Re-enrich it (creates new enriched task object)
 * 2. Otherwise (task unchanged):
 *    - Look up previously enriched version in previousEnrichedMap
 *    - Reuse it (preserves object reference, avoids re-enrichment cost)
 *    - Fallback to re-enrichment if not found in cache (shouldn't happen)
 *
 * This approach minimizes object creation and ensures unchanged tasks
 * maintain stable references, preventing unnecessary downstream re-renders.
 *
 * @param currentTasks - Current tasks array (maintains original task order)
 * @param containersMap - Map of containerId -> Container with path information
 * @param previousEnrichedMap - Map of previously enriched tasks (taskId -> enriched task)
 * @param changedTaskIds - Set of task IDs that have changed (need re-enrichment)
 * @returns Array of enriched tasks (same order as currentTasks, reusing unchanged tasks from cache)
 */
function applyIncrementalEnrichment(
  currentTasks: Task[],
  containersMap: Map<string, Container>,
  previousEnrichedMap: Map<string, EnrichedTask>,
  changedTaskIds: Set<string>
): EnrichedTask[] {
  // If no containers available, return tasks as-is
  if (containersMap.size === 0) {
    return currentTasks;
  }

  const enrichedTasks: EnrichedTask[] = [];

  for (const task of currentTasks) {
    if (changedTaskIds.has(task.id)) {
      // Task has changed - re-enrich it
      const enrichedTask = enrichSingleTask(task, containersMap);
      enrichedTasks.push(enrichedTask);
    } else {
      // Task unchanged - reuse previous enriched version if available
      const previousEnriched = previousEnrichedMap.get(task.id);
      if (previousEnriched) {
        enrichedTasks.push(previousEnriched);
      } else {
        // Shouldn't happen, but enrich as fallback
        enrichedTasks.push(enrichSingleTask(task, containersMap));
      }
    }
  }

  return enrichedTasks;
}

/**
 * Hook to enrich tasks with container information (spaceName, folderName, listName, path).
 * Also calculates unique values (assignees, tags, etc.) from enriched tasks.
 *
 * OPTIMIZATION STRATEGY - Incremental Updates:
 * Instead of re-enriching all tasks on every render (O(n) complexity), this hook uses
 * incremental updates to only process changed tasks:
 *
 * 1. Hash-based Change Detection:
 *    - Creates hash for each task using id:updatedAt (see createTaskHash)
 *    - Compares current hashes vs previous hashes to detect which tasks changed
 *    - Only tasks with different hashes need re-enrichment
 *
 * 2. Caching Strategy:
 *    - Uses useRef to persist enriched tasks between renders
 *    - Reuses cached enriched tasks for unchanged tasks (avoiding object creation)
 *    - Only creates new enriched task objects for changed/new tasks
 *
 * 3. Container Change Handling:
 *    - Detects when containersMap changes (by reference comparison)
 *    - Forces re-enrichment of ALL tasks when containers change
 *    - Necessary because container paths/names may have changed
 *
 * PERFORMANCE BENEFITS:
 * - With 8000+ tasks, typical single-task update only re-enriches 1 task (not 8000)
 * - Prevents unnecessary object creation and downstream re-renders
 * - Reduces enrichment time from O(n) to O(changed tasks)
 * - Logs performance metrics (reuse %, time saved) for monitoring
 *
 * @param options - Enrichment options
 * @param options.tasks - Array of tasks to enrich
 * @param options.containersMap - Map of containerId -> Container with path information
 * @returns Object with enriched tasks and unique values
 * @returns enrichedTasks - Tasks with additional fields (spaceName, folderPath, listName, path)
 * @returns uniqueValues - Object with arrays of unique values (assignees, tags, etc.)
 */
export function useTaskEnrichment({ tasks, containersMap }: UseTaskEnrichmentOptions) {
  // Ensure containersMap is always a Map (safety check at function level)
  // This prevents any undefined/null access errors
  const safeContainersMap = useMemo(() => {
    if (containersMap instanceof Map) {
      return containersMap;
    }
    return new Map<string, Container>();
  }, [containersMap]);

  // Track previous state for incremental enrichment
  // Follows useRef pattern from useSpaces (lines 63-64)
  const previousTaskHashMapRef = useRef<Map<string, string>>(new Map());
  const previousEnrichedTasksMapRef = useRef<Map<string, EnrichedTask>>(new Map());
  const previousContainersMapRef = useRef<Map<string, Container>>(new Map());

  // Enrich tasks with path and derived fields (spaceName, folderPath, listName)
  // OPTIMIZATION: Use incremental enrichment to only process changed tasks
  // NOTE: This useMemo intentionally reads/writes refs for incremental caching optimization.
  // The refs are used to track previous state and avoid re-enriching unchanged tasks.
  // This is safe because the refs are only used to compare/cache values, not for rendering.
  /* eslint-disable react-hooks/refs */
  const enrichedTasks = useMemo(() => {
    // Early return: If containersMap is empty, return tasks as-is (no enrichment possible)
    if (safeContainersMap.size === 0) {
      return tasks;
    }

    // Step 1: Detect if containersMap changed (by reference comparison)
    // When containersMap changes, all tasks need re-enrichment because:
    // - Container names may have changed (e.g., list renamed)
    // - Container paths may have changed (e.g., list moved to different folder)
    // - Cached enriched data would be stale
    const containersMapChanged = previousContainersMapRef.current !== safeContainersMap;

    // Step 2: Detect which tasks have changed since last enrichment
    // Uses hash-based comparison (id:updatedAt) to identify:
    // - New tasks (not in previous hash map)
    // - Updated tasks (different updatedAt timestamp)
    const { changedTaskIds, taskHashMap } = detectChangedTasks(
      tasks,
      previousTaskHashMapRef.current
    );

    // Step 3: Determine effective set of tasks to re-enrich
    // If containersMap changed: re-enrich ALL tasks (cached data is invalid)
    // Otherwise: only re-enrich tasks that changed (optimization path)
    const effectiveChangedTaskIds = containersMapChanged
      ? new Set(tasks.map(t => t.id))
      : changedTaskIds;

    // Step 4: Apply incremental enrichment
    // For each task:
    // - If task ID in effectiveChangedTaskIds: re-enrich (creates new object)
    // - Otherwise: reuse cached enriched task (no object creation, preserves reference)
    // This is the key optimization: unchanged tasks reuse previous enriched objects
    const enriched = applyIncrementalEnrichment(
      tasks,
      safeContainersMap,
      previousEnrichedTasksMapRef.current,
      effectiveChangedTaskIds
    );

    // Step 5: Update refs for next iteration
    // Store current hash map for next change detection cycle
    previousTaskHashMapRef.current = taskHashMap;
    // Store current containersMap reference for next containersMap change detection
    previousContainersMapRef.current = safeContainersMap;

    // Step 6: Build new enriched tasks map for next iteration
    // This map is used by applyIncrementalEnrichment to look up cached enriched tasks
    // Maps taskId -> enriched task for O(1) lookup performance
    const newEnrichedTasksMap = new Map<string, EnrichedTask>();
    for (const enrichedTask of enriched) {
      newEnrichedTasksMap.set(enrichedTask.id, enrichedTask);
    }
    previousEnrichedTasksMapRef.current = newEnrichedTasksMap;

    return enriched;
  }, [tasks, safeContainersMap]);
  /* eslint-enable react-hooks/refs */

  const uniqueValues = useMemo(() => getUniqueValues(enrichedTasks), [enrichedTasks]);

  return {
    enrichedTasks,
    uniqueValues,
  };
}

